import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { checkNotInRecipe, saveAnswers, mergeIngredient, undoMerge } from './notInRecipeApi';
import NotInRecipeCard, { money } from './NotInRecipeCard';
import { onDataChanged } from '../../utils/dataChangeSignal';
import '../notInMenu/NotInMenu.css';
import './NotInRecipe.css';

const SECTIONS = [
  { key: 'needs_choice', title: 'Needs a choice' },
  { key: 'added_to_recipe', title: 'Added to a recipe this month' },
  { key: 'ready', title: 'Ready', collapsible: true },
];
/** Ready collapses behind "Show" when it holds more cards than this (spec §6.2). */
const READY_COLLAPSE_AFTER = 5;
const MERGE_BLOCKED = 'Merge before starting the count';

/**
 * Answers typed but not yet confirmed, per closing date, for this browser session. "Add to
 * recipe" leaves for Item Mapping (it cannot open over this screen), and coming back must
 * not lose the taps already made (spec §9.7).
 */
const memory = new Map();

const allCards = (data) => (data && data.sections
  ? [...(data.sections.needs_choice || []), ...(data.sections.added_to_recipe || []), ...(data.sections.ready || [])]
  : []);
/** A card still waiting: no head chosen, or a rate needed and none typed above 0. */
const waiting = (card, choice) => !(choice && choice.head)
  || (card.flags.includes('NO_PRICE') && !(Number(choice.rate) > 0));

/**
 * Not in Recipe (spec 2026-10-27 §9): every item no dish uses that moved this month, before
 * the count that closes it. One expense head each; Merge folds a duplicate name into the real
 * item. Looks and works like the Not in Menu worklist.
 *
 * onProceed (the caller): after the answers are saved, check again and move on — resolves
 * { ok: true }, or { ok: false, error } to keep the popup open with the reason.
 */
export default function NotInRecipePopup({
  open, through, fromApply = false, applyMessage = null, onClose, onProceed, onAddToRecipe,
}) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [choices, setChoices] = useState({});
  const [filter, setFilter] = useState('');
  const [showReady, setShowReady] = useState(false);
  const [mergeFor, setMergeFor] = useState(null);
  const [mergeErrors, setMergeErrors] = useState({});
  const [undoErrors, setUndoErrors] = useState({});
  const [rateErrors, setRateErrors] = useState({});
  const [merging, setMerging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [footerError, setFooterError] = useState(null);
  const [notice, setNotice] = useState(null);
  // A ref, not state: two taps in one tick both see saving === false.
  const savingRef = useRef(false);
  const bodyRef = useRef(null);

  const load = useCallback(async () => {
    setLoadError(null);
    const r = await checkNotInRecipe(through);
    if (!r.ok) { setData(null); setLoadError("Couldn't load the items."); return; }
    setData(r);
    const remembered = memory.get(through) || {};
    setChoices((prev) => {
      const next = {};
      for (const card of allCards(r)) {
        next[card.item_id] = prev[card.item_id] || remembered[card.item_id]
          || { head: card.head || null, count_at_close: card.count_at_close === true, rate: '' };
      }
      return next;
    });
  }, [through]);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setData(null); setChoices({}); setFilter(''); setShowReady(false); setMergeFor(null);
    setMergeErrors({}); setUndoErrors({}); setRateErrors({}); setFooterError(null); setNotice(null);
    load();
  }, [open, load]);

  // Back from Item Mapping (another tab, or a recipe saved): the list is read again (§9.7).
  useEffect(() => {
    if (!open) return undefined;
    const stop = onDataChanged(() => { if (!savingRef.current) load(); });
    const onFocus = () => { if (!savingRef.current) load(); };
    window.addEventListener('focus', onFocus);
    return () => { stop(); window.removeEventListener('focus', onFocus); };
  }, [open, load]);

  useEffect(() => {
    if (data && Object.keys(choices).length > 0) memory.set(through, choices);
  }, [choices, data, through]);

  const cards = useMemo(() => allCards(data), [data]);
  const left = cards.filter((c) => waiting(c, choices[c.item_id]));
  const draftExists = fromApply || Boolean(data && data.closing_draft_exists);
  const busy = saving || merging;

  const setChoice = (itemId, patch) => {
    setChoices((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), ...patch } }));
  };
  const choose = (itemId, head) => {
    setChoice(itemId, { head });
    if (mergeFor === itemId) setMergeFor(null);   // tapping a head pill closes the merge picker
  };

  const merge = async (card, target) => {
    setMerging(true);
    setMergeErrors((e) => ({ ...e, [card.item_id]: null }));
    const r = await mergeIngredient(through, card.item_id, target.item_id);
    setMerging(false);
    if (!r.ok) {
      setMergeErrors((e) => ({ ...e, [card.item_id]: r.error }));
      return;
    }
    setMergeFor(null);
    setNotice(`${card.name} merged into ${target.name}.`);
    await load();
  };

  const undo = async (m) => {
    setMerging(true);
    setUndoErrors((e) => ({ ...e, [m.log_id]: null }));
    const r = await undoMerge(m.log_id);
    setMerging(false);
    if (!r.ok) {
      setUndoErrors((e) => ({ ...e, [m.log_id]: r.details ? `${r.error} ${r.details}` : r.error }));
      return;
    }
    setNotice(`${m.source_name} is its own item again.`);
    await load();
  };

  const scrollToFirst = () => {
    if (!left[0] || !bodyRef.current) return;
    const el = bodyRef.current.querySelector(`[data-item-id="${left[0].item_id}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const confirm = async () => {
    if (savingRef.current || !data || left.length > 0 || merging) return;
    savingRef.current = true;
    setSaving(true); setFooterError(null); setRateErrors({});
    try {
      const answers = cards.map((c) => {
        const ch = choices[c.item_id] || {};
        return {
          item_id: c.item_id,
          head: ch.head,
          ...(c.is_non_consumable ? { count_at_close: Boolean(ch.count_at_close) } : {}),
          ...(c.flags.includes('NO_PRICE') && String(ch.rate || '').trim() !== '' ? { rate: Number(ch.rate) } : {}),
        };
      });
      const saved = await saveAnswers(through, answers);
      if (!saved.ok) {
        if (saved.code === 'BAD_RATE' && saved.itemId !== null) {
          setRateErrors({ [saved.itemId]: 'Enter a rate above 0' });
        }
        setFooterError(saved.error);
        if (saved.code === 'ITEM_NOT_FOUND') await load();
        return;
      }
      let r;
      try {
        r = await onProceed();
      } catch (err) {
        r = { ok: false, error: err?.message || 'Could not continue.' };
      }
      if (r && r.ok) { memory.delete(through); return; }
      setFooterError((r && r.error) || 'Some items still need a head or a rate.');
      await load();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const q = filter.trim().toLowerCase();
  const match = (c) => !q || String(c.name || '').toLowerCase().includes(q) || String(c.category || '').toLowerCase().includes(q);

  if (!open) return null;
  const merged = (data && data.sections && data.sections.merged) || [];
  const estimate = Boolean(data && data.estimate);

  return (
    <div className="nim-overlay nir-overlay" onClick={busy ? undefined : onClose}>
      <div className="nim-container" role="dialog" aria-label="Not in Recipe" onClick={(e) => e.stopPropagation()}>
        <div className="nim-header">
          <div>
            <h3 className="nim-title">Not in Recipe</h3>
            <p className="nim-subtitle">
              No dish uses these items, so they&apos;ll be booked as {data && data.month_label ? `${data.month_label} ` : ''}expenses.
              {' '}If a dish does use one, add it in Item Mapping.
            </p>
          </div>
          <button type="button" className="nim-close" aria-label="Close" onClick={onClose} disabled={busy}>×</button>
        </div>
        <div className="nim-body" ref={bodyRef}>
          {applyMessage && <div className="um-warn nir-apply-note" role="status">{applyMessage}</div>}
          {notice && <div className="um-notice" role="status">{notice}</div>}
          {loadError && (
            <div className="um-error" role="alert">
              {loadError} <button type="button" className="um-undo-btn" onClick={load}>Retry</button>
            </div>
          )}
          {!data && !loadError && (
            <div className="nim-loading-state">
              <div className="nim-spinner" />
              <p className="nim-loading-text">Loading…</p>
            </div>
          )}
          {data && (
            <>
              <div className="nir-tiles">
                <div className="nir-tile">
                  <span className="nir-tile-label">Items</span>
                  <span className="nir-tile-value">{cards.length}</span>
                </div>
                <div className="nir-tile">
                  <span className="nir-tile-label">To be booked</span>
                  <span className="nir-tile-value">{estimate ? '≈ ' : ''}{money(data.total_amount)}</span>
                </div>
                <div className={`nir-tile${left.length > 0 ? ' nir-tile-warn' : ''}`}>
                  <span className="nir-tile-label">Need a choice</span>
                  <span className="nir-tile-value">{left.length}</span>
                </div>
              </div>
              <input className="nim-filter" type="search" placeholder="Filter items…" aria-label="Filter items"
                value={filter} onChange={(e) => setFilter(e.target.value)} />
              {SECTIONS.map(({ key, title, collapsible }) => {
                const list = ((data.sections && data.sections[key]) || []).filter(match);
                if (list.length === 0) return null;
                const collapsed = collapsible && list.length > READY_COLLAPSE_AFTER && !showReady;
                return (
                  <section key={key} className="nim-section">
                    <div className="nir-section-head">
                      <h4 className="nim-section-title">{title} ({list.length})</h4>
                      {collapsible && list.length > READY_COLLAPSE_AFTER && (
                        <button type="button" className="nir-show-link" onClick={() => setShowReady((v) => !v)}>
                          {showReady ? 'Hide' : 'Show'}
                        </button>
                      )}
                    </div>
                    {!collapsed && list.map((card) => (
                      <NotInRecipeCard
                        key={card.item_id}
                        card={card}
                        month={data.month}
                        choice={choices[card.item_id]}
                        busy={busy}
                        ingredients={data.ingredients || []}
                        mergeOpen={mergeFor === card.item_id}
                        mergeDisabledReason={draftExists ? MERGE_BLOCKED : null}
                        mergeError={mergeErrors[card.item_id]}
                        rateError={rateErrors[card.item_id]}
                        countItNote={draftExists
                          ? 'Counts from next month — this month\'s sheet is already filled.'
                          : 'On this month\'s sheet.'}
                        onChoose={(head) => choose(card.item_id, head)}
                        onToggleMerge={() => setMergeFor((cur) => (cur === card.item_id ? null : card.item_id))}
                        onCountIt={(v) => setChoice(card.item_id, { count_at_close: v })}
                        onRate={(v) => setChoice(card.item_id, { rate: v })}
                        onMerge={(target) => merge(card, target)}
                        onAddToRecipe={onAddToRecipe ? () => onAddToRecipe(card) : null}
                      />
                    ))}
                  </section>
                );
              })}
              {merged.length > 0 && (
                <section className="nim-section">
                  <h4 className="nim-section-title">Merged ({merged.length})</h4>
                  {merged.map((m) => (
                    <div key={m.log_id || `${m.source_item_id}-${m.target_item_id}`}>
                      <div className="nir-merged-row">
                        <span>{m.source_name} → {m.target_name}</span>
                        {m.booked
                          ? <span className="um-muted">Booked — undo from Inventory</span>
                          : <button type="button" className="um-undo-btn" disabled={busy || !m.log_id} onClick={() => undo(m)}>Undo</button>}
                      </div>
                      {undoErrors[m.log_id] && <div className="um-error" role="alert">{undoErrors[m.log_id]}</div>}
                    </div>
                  ))}
                </section>
              )}
              {cards.length === 0 && merged.length === 0 && (
                <p className="um-muted">Nothing to review — every item that moved this month is used by a dish.</p>
              )}
            </>
          )}
        </div>
        <div className="nim-footer nim-proceed-footer">
          {data && (
            <div className="nir-footer-status">
              {left.length > 0
                ? <button type="button" className="nir-left" onClick={scrollToFirst}>{left.length} item{left.length === 1 ? '' : 's'} left</button>
                : <span className="nir-all-done">All items have a choice</span>}
            </div>
          )}
          {footerError && <div className="um-error nim-proceed-error" role="alert">{footerError}</div>}
          <button type="button" className="um-primary-btn nim-proceed-btn"
            disabled={!data || busy || left.length > 0} aria-busy={saving} onClick={confirm}>
            {saving ? 'Saving…' : 'Confirm and proceed'}
          </button>
        </div>
      </div>
    </div>
  );
}
